/*
 * @format
 *
 * Per-interface DESIRED state: the pending control changes other clients have
 * requested for a device, projected by the backend. This is kept entirely
 * separate from the home objects (which describe REPORTED state) so telemetry
 * rebuilds never touch it.
 *
 * Slice shape: { [interfaceId]: { family, stateVersion, goals, settling? } }
 *
 * settling is an OPTIONAL list of recently-completed control goals retained as a
 * short-lived overlay so an OBSERVING phone (one that did not make the change)
 * does not flicker its display back to the stale reported value in the gap
 * between a goal completing (goals emptied) and this phone's own reported
 * telemetry catching up. A resolved:'success' outcome is a trustworthy "device
 * reached the goal" signal (the reconciler only marks success once REPORTED
 * matched the goal), so retaining its value is not a guess. The overlay is
 * consulted by getSharedPending and clears — from the display via that selector
 * self-healing once reported matches, and from this slice via the
 * desired-state-settling saga's prune (reported-match or TTL backstop) plus
 * supersession when a newer goal or block arrives for the zone. The key is
 * omitted whenever the list is empty so the common slice shape is unchanged.
 *
 * The same desiredState block arrives from two places — a live websocket push
 * and the desiredState attribute on each aws-target home in the bootstrap
 * response — and both are ingested through one last-writer-wins rule.
 *
 * A home's desiredState block is present only when the backend's per-home read
 * returned one, which it does NOT in two distinct cases: the device is no longer
 * aws-target, OR it IS aws-target but its desired-state read transiently failed
 * (the backend isolates that fault and omits the block for that one home). So a
 * missing block cannot by itself mean "left aws" and must NEVER drive a prune —
 * evicting on a transient fault would drop a still-live cross-client goal.
 *
 * Pruning keys instead on the home's DEFINITIVE routing target,
 * haloDeviceServiceTarget: a home present in the complete homes bootstrap whose
 * target is explicitly non-aws ('cf' / 'legacy') has genuinely left aws, so its
 * cached goal is dropped. An aws-target home missing its block is that transient
 * read fault — kept, to heal on the next successful read/push. Absence from the
 * snapshot never prunes (it may be a partial view); this is confined to the
 * homes bootstrap, the user's complete list (see the fold below).
 */

import * as actionTypes from '../action-types';

export const initialState = {};

// A settling entry can never linger past this window if reported telemetry
// never confirms it. The saga's reported-match prune clears it far sooner in
// the normal case; this is only the backstop for the pathological "telemetry
// never lands" case. Exported so the saga applies the identical bound.
export const SETTLING_TTL_MS = 2 * 60 * 1000;

// A control goal is per-zone (prtId + endpointId); settling is deduped/matched
// on that pair.
const zoneKey = (prtId, endpointId) => `${prtId}#${endpointId}`;

// Recompute an interface's settling list when a block is ingested. Additions
// come from resolved:'success' outcomes matched — by clientRequestId — against
// the goals the slice held BEFORE this block replaced them (the completed goal
// is already gone from the incoming goals). Only control goals are retained,
// since only they drive the mode/setpoint display. Prior entries are carried
// forward except when a newer goal for the same zone supersedes them, a fresh
// addition replaces them, or the TTL backstop has elapsed.
const buildSettling = (existing, block, now) => {
  const prior = Array.isArray(existing && existing.settling)
    ? existing.settling
    : [];
  const priorGoals = Array.isArray(existing && existing.goals)
    ? existing.goals
    : [];
  const incomingGoals = Array.isArray(block.goals) ? block.goals : [];
  const resolved = Array.isArray(block.resolved) ? block.resolved : [];

  // (1) new settling from successful outcomes, valued from the prior goal.
  const additions = [];
  resolved.forEach(outcome => {
    if (
      !outcome ||
      outcome.outcome !== 'success' ||
      !outcome.clientRequestId
    ) {
      return;
    }
    const goal = priorGoals.find(
      g =>
        g &&
        g.kind === 'control' &&
        g.clientRequestId === outcome.clientRequestId,
    );
    if (!goal) {
      return;
    }
    additions.push({
      mode: goal.mode,
      setpoint: goal.setpoint,
      // Carry holdDuration/holidayEndTime too, mirroring getSharedPending's
      // live-goal branch: an OverridePlusHours boost's countdown
      // (getPlusHoursEndtime -> shared.holdDuration) must stay populated through
      // the settle window, else the time-remaining sub-display flickers to blank.
      holdDuration: goal.holdDuration,
      holidayEndTime: goal.holidayEndTime,
      prtId: goal.prtId,
      endpointId: goal.endpointId,
      clientRequestId: goal.clientRequestId,
      requestedAt: goal.requestedAt,
      settledAt: now,
    });
  });

  // A zone that carries a fresh control goal in this block has a live target
  // again — any settling for it is superseded.
  const hasFreshGoalForZone = (prtId, endpointId) =>
    incomingGoals.some(
      g =>
        g &&
        g.kind === 'control' &&
        g.prtId === prtId &&
        g.endpointId === endpointId,
    );

  const replacedZones = new Set(
    additions.map(a => zoneKey(a.prtId, a.endpointId)),
  );

  const kept = prior.filter(
    entry =>
      entry &&
      now - entry.settledAt <= SETTLING_TTL_MS &&
      !hasFreshGoalForZone(entry.prtId, entry.endpointId) &&
      !replacedZones.has(zoneKey(entry.prtId, entry.endpointId)),
  );

  // Defensive: a success normally removes the goal from the incoming block, but
  // never retain an addition for a zone that still carries a live goal.
  const addable = additions.filter(
    a => !hasFreshGoalForZone(a.prtId, a.endpointId),
  );

  return [...kept, ...addable];
};

// Assemble the stored entry, attaching settling only when it is non-empty so
// the common slice shape ({ family, stateVersion, goals }) is unchanged.
const withSettling = (entry, settling) =>
  settling.length > 0 ? { ...entry, settling } : entry;

// Apply a single desiredState block under last-writer-wins on stateVersion.
// The block wins only when it is strictly newer than the entry we already hold;
// equal or lower versions are dropped so replays and stale snapshots are
// idempotent (the original state reference is returned unchanged). Goals are
// stored verbatim — units are opaque and must never be converted here.
const ingestBlock = (state, block, now = Date.now()) => {
  if (!block || !block.interfaceId) {
    return state;
  }

  const { interfaceId, family, stateVersion, goals } = block;
  const existing = state[interfaceId];

  if (existing && !(stateVersion > existing.stateVersion)) {
    return state;
  }

  const settling = buildSettling(existing, block, now);

  return {
    ...state,
    [interfaceId]: withSettling({ family, stateVersion, goals }, settling),
  };
};

// Drop a cached entry for an interface whose home the complete homes bootstrap
// shows is no longer aws-target (its routing target is definitively non-aws; see
// the homes fold). No stateVersion to honour here — the routing target IS the
// signal, and it is only trusted on the homes bootstrap. Returns the same
// reference when there is nothing to remove (idempotent). The slice is keyed by
// home.id, which is the interfaceId the block would have carried.
const pruneInterface = (state, interfaceId) => {
  if (!interfaceId || !state[interfaceId]) {
    return state;
  }

  const next = { ...state };
  delete next[interfaceId];
  return next;
};

// A home is DEFINITIVELY no longer aws-target only when its routing target is
// present and not 'aws' ('cf' / 'legacy'). A missing/blank target — an older
// backend that does not serialise it yet — is not definitive and never drives a
// prune; nor does 'aws' itself (a blockless aws home is a transient read fault).
// Used by the homes-bootstrap prune fold below so eviction keys on the
// DEFINITIVE routing target rather than raw block-absence. (Still exported, but
// with no external consumer since the desired-state-heal saga was removed.)
export const isDefinitivelyNonAws = home =>
  !!home.haloDeviceServiceTarget && home.haloDeviceServiceTarget !== 'aws';

// The two bootstrap actions carry the homes array under different keys.
const homesFromAction = action => {
  if (action.type === actionTypes.HOMES_MESSAGE_RECEIVED) {
    return action.data?.data;
  }

  // hello message: filteredHomes is a legacy fallback for older backends
  return action.data?.homes || action.data?.filteredHomes;
};

export default (state = initialState, action) => {
  switch (action.type) {
    // Drop all cached desired state on sign-out so one user's pending control
    // goals and request ids never linger into another user's session on a
    // shared device (mirrors the sibling slices' sign-out reset).
    case actionTypes.LOGOUT_COMPLETED:
      return initialState;

    case actionTypes.WEBSOCKET_DESIRED_STATE_RECEIVED:
      return ingestBlock(state, action.data);

    // Drop settling entries the saga has decided are done (reported matched, or
    // the TTL backstop elapsed). Only the ids cross the boundary; the reported
    // comparison stays out of this slice. Returns the same reference when there
    // is nothing to remove, and omits the settling key once it empties.
    case actionTypes.DESIRED_STATE_SETTLING_PRUNED: {
      const { interfaceId, clientRequestIds } = action;
      const entry = interfaceId && state[interfaceId];

      if (
        !entry ||
        !Array.isArray(entry.settling) ||
        entry.settling.length === 0 ||
        !Array.isArray(clientRequestIds) ||
        clientRequestIds.length === 0
      ) {
        return state;
      }

      const toRemove = new Set(clientRequestIds);
      const settling = entry.settling.filter(
        s => !toRemove.has(s.clientRequestId),
      );

      if (settling.length === entry.settling.length) {
        return state;
      }

      const { family, stateVersion, goals } = entry;
      return {
        ...state,
        [interfaceId]: withSettling({ family, stateVersion, goals }, settling),
      };
    }

    // The HTTP homes bootstrap: the user's COMPLETE home list, each aws-target
    // home carrying its desiredState block inline (findAll('home')). LWW-ingest
    // every home that has a block. For a home with NO block, prune its cached
    // goal ONLY when its routing target is definitively non-aws — the device has
    // genuinely left 'aws'. A blockless home that is still aws-target is a
    // transient desired-state read fault, NOT a signal to evict: keep the cached
    // goal so a live cross-client goal survives, healing on the next read/push.
    // A home absent from the list is never touched (see pruneInterface).
    case actionTypes.HOMES_MESSAGE_RECEIVED: {
      const homes = homesFromAction(action);

      if (!homes || homes.length === 0) {
        return state;
      }

      return homes.reduce((acc, home) => {
        if (!home || !home.id) {
          return acc;
        }

        if (home.desiredState) {
          return ingestBlock(acc, home.desiredState);
        }

        return isDefinitivelyNonAws(home)
          ? pruneInterface(acc, home.id)
          : acc;
      }, state);
    }

    // The websocket hello: its homes array does NOT carry desiredState blocks
    // (the backend sends those as separate per-device desiredState pushes after
    // the reply) and can be a filtered/partial view. Present-without-block here
    // therefore does NOT mean "left aws", so this fold must NEVER prune — doing
    // so would evict every aws device's goal on each hello. Ingest only, kept
    // defensively for any home that does happen to carry a block.
    case actionTypes.WEBSOCKET_HELLO_MESSAGE_RECEIVED: {
      const homes = homesFromAction(action);

      if (!homes || homes.length === 0) {
        return state;
      }

      return homes.reduce(
        (acc, home) =>
          home && home.desiredState
            ? ingestBlock(acc, home.desiredState)
            : acc,
        state,
      );
    }

    default:
      return state;
  }
};
